import type { ErrorCode } from '@irace/shared';
import type { OutboxStatus } from './outbox-record.ts';

/**
 * G-011 — the shape of one locally saved check-out.
 *
 * Why this is not a field on `OutboxRecord`, and why it needs its own object
 * store: a check-out is identified by the submission_id of the check-in it
 * belongs to. That shared identity is the whole idempotency story on the
 * server, and it is also what makes one store impossible here — the outbox is
 * keyed by `submissionId`, so writing a check-out into it would overwrite the
 * check-in under the same key and destroy a record the participant cannot
 * recreate. Two stores, one key space, no collision.
 *
 * As with the outbox, this record is written before any network call
 * (AGENTS.md invariant 2) and must never contain a Turnstile token
 * (invariant 3) — every attempt fetches a fresh one.
 */

export const CHECKOUT_SCHEMA_VERSION = 1;

export interface CheckoutRecord {
  /** Bumped when this shape changes; old records migrate in place. */
  schemaVersion: number;
  /**
   * The check-in's submission_id, never a new one. A check-out that minted its
   * own id could not be matched to the arrival it closes (AGENTS.md
   * invariant 4).
   */
  submissionId: string;
  eventId: string;
  /** When the participant said they left, as recorded on the device. */
  clientCheckedOutAt: string;
  status: OutboxStatus;
  attemptCount: number;
  lastAttemptAt?: string;
  /** Why the last attempt did not end in 202. Never contains a token. */
  lastErrorCode?: ErrorCode | 'NETWORK' | 'TURNSTILE_CLIENT' | 'UNKNOWN_REJECTED';
}

/** Records the retry path is allowed to pick up. */
export function isCheckoutRetryable(record: CheckoutRecord): boolean {
  return record.status === 'pending' || record.status === 'sending';
}

/**
 * Read a record written by an older bundle.
 *
 * Mirrors `migrateRecord`: a Service Worker update must never orphan a saved
 * check-out, so an older record is upgraded field by field rather than
 * dropped, while a record from a *newer* schema is left untouched because
 * guessing at fields we do not know about would corrupt it.
 */
export function migrateCheckoutRecord(stored: unknown): CheckoutRecord | null {
  if (typeof stored !== 'object' || stored === null) return null;
  const record = stored as Partial<CheckoutRecord>;

  if (
    typeof record.submissionId !== 'string' ||
    typeof record.eventId !== 'string' ||
    typeof record.clientCheckedOutAt !== 'string'
  ) {
    return null;
  }

  return {
    ...record,
    schemaVersion:
      typeof record.schemaVersion === 'number' ? record.schemaVersion : CHECKOUT_SCHEMA_VERSION,
    submissionId: record.submissionId,
    eventId: record.eventId,
    clientCheckedOutAt: record.clientCheckedOutAt,
    status: record.status ?? 'pending',
    attemptCount: typeof record.attemptCount === 'number' ? record.attemptCount : 0,
  };
}
